/**
 * useJobsTable — Polls GET /api/{module}/jobs every `intervalMs` ms.
 * Used inside JobsTable; exported for testing / custom consumers.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { HistoryJob } from '@/types'

export interface JobTableClient {
  listJobs:  () => Promise<HistoryJob[]>
  cancelJob: (id: string) => Promise<unknown>
}

export function useJobsTable(client: JobTableClient, intervalMs = 5_000) {
  const [jobs,    setJobs]    = useState<HistoryJob[]>([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)

  // Keep a stable ref so the interval callback always sees the latest client
  const clientRef = useRef<JobTableClient>(client)
  clientRef.current = client

  const load = useCallback(async () => {
    try {
      const data = await clientRef.current.listJobs()
      setJobs(data)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load jobs')
    } finally {
      setLoading(false)
    }
  }, [])

  const deleteJob = useCallback(async (id: string) => {
    try {
      await clientRef.current.cancelJob(id)
      setJobs(prev => prev.filter(j => j.job_id !== id))
    } catch (e) {
      console.error('Failed to delete job', id, e)
    }
  }, [])

  useEffect(() => {
    load()
    const timer = setInterval(load, intervalMs)
    return () => clearInterval(timer)
  }, [load, intervalMs])

  return { jobs, loading, error, refresh: load, deleteJob }
}
